import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Popconfirm, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { TableProps } from 'antd';
import { AsyncBoundary, formatDateTime, space } from '@viq-admin/ui';
import {
  getConferenceAutoEnrichment, startConferenceAutoEnrichment, stopConferenceAutoEnrichment,
  type AutoEnrichCompanyLine,
} from '../../api/autoEnrichment';
import { Can } from '../../ability/AbilityContext';
import { CompanyLink } from '../../components/EntityLinks';
import { AUTO_ENRICH_PAST_COPY } from './autoEnrichPastLeadsCopy';

/**
 * "Auto enrichment for past leads": a manual start of the AUTOMATIC run for
 * leads already captured at this show, one line per company with access. Run
 * queues that company's leads for the same background job new leads go
 * through; Stop takes them back out. It is deliberately not the lead page's
 * Enrich button, and the copy says so. The backend gates the writes on
 * `manage Lead` and the company's "Auto enrichment" flag; the <Can> and the
 * disabled button only mirror that.
 */


/** Run / Run again and Stop for one company line; nothing for a caller without `manage Lead`. */
function LineAction({ line, onRun, onStop, running, stopping }: {
  line: AutoEnrichCompanyLine; onRun: () => void; onStop: () => void; running: boolean; stopping: boolean;
}) {
  const withoutPhone = line.leads - line.withPhone;
  const why = line.archived
    ? AUTO_ENRICH_PAST_COPY.archived
    : !line.autoEnrich
      ? AUTO_ENRICH_PAST_COPY.flagOff
      : withoutPhone === 0
        ? AUTO_ENRICH_PAST_COPY.noLeadsWithoutPhone
        : null;
  return (
    <Can I="manage" a="Lead" passThrough>
      {(allowed: boolean) =>
        allowed ? (
          <Space size={space.xs} wrap>
            <Tooltip title={why}>
              <span>
                <Popconfirm title={AUTO_ENRICH_PAST_COPY.confirmRun(line.companyName, withoutPhone)} onConfirm={onRun} disabled={why !== null}>
                  <Button type={line.backfill ? 'default' : 'primary'} disabled={why !== null} loading={running}>
                    {line.backfill ? AUTO_ENRICH_PAST_COPY.runAgain : AUTO_ENRICH_PAST_COPY.run}
                  </Button>
                </Popconfirm>
              </span>
            </Tooltip>
            {line.backfill ? (
              <Popconfirm title={AUTO_ENRICH_PAST_COPY.confirmStop} onConfirm={onStop}>
                <Button danger loading={stopping}>{AUTO_ENRICH_PAST_COPY.stop}</Button>
              </Popconfirm>
            ) : null}
          </Space>
        ) : null
      }
    </Can>
  );
}

const queryKey = (conferenceId: string) => ['conferenceOverview', conferenceId, 'autoEnrichment'] as const;

export function AutoEnrichPastLeads({ conferenceId }: { conferenceId: string }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKey(conferenceId),
    queryFn: () => getConferenceAutoEnrichment(conferenceId),
  });
  const onDone = (lines: AutoEnrichCompanyLine[]) => queryClient.setQueryData(queryKey(conferenceId), lines);
  const onError = (err: unknown) => message.error((err as { message?: string }).message ?? 'Something went wrong');
  const start = useMutation({
    mutationFn: (companyId: string) => startConferenceAutoEnrichment(conferenceId, companyId),
    onSuccess: (lines) => { onDone(lines); message.success(AUTO_ENRICH_PAST_COPY.started); },
    onError,
  });
  const stop = useMutation({
    mutationFn: (companyId: string) => stopConferenceAutoEnrichment(conferenceId, companyId),
    onSuccess: (lines) => { onDone(lines); message.success(AUTO_ENRICH_PAST_COPY.stopped); },
    onError,
  });

  const columns: NonNullable<TableProps<AutoEnrichCompanyLine>['columns']> = [
    {
      key: 'company',
      title: 'Company',
      render: (_: unknown, l: AutoEnrichCompanyLine) => (
        <Space direction="vertical" size={0}>
          <Space size={space.xs} wrap>
            <CompanyLink id={l.companyId} name={l.companyName} />
            {l.backfill ? <Tag color="processing">{AUTO_ENRICH_PAST_COPY.queuedTag}</Tag> : null}
          </Space>
          {l.backfill ? (
            <Typography.Text type="secondary">
              {AUTO_ENRICH_PAST_COPY.queuedBy(formatDateTime(l.backfill.requestedAt), l.backfill.requestedBy)}
            </Typography.Text>
          ) : null}
        </Space>
      ),
    },
    {
      key: 'flag',
      title: 'Auto enrichment',
      width: 130,
      render: (_: unknown, l: AutoEnrichCompanyLine) => (l.autoEnrich ? <Tag color="green">On</Tag> : <Tag>Off</Tag>),
    },
    { key: 'leads', title: 'Leads', dataIndex: 'leads', align: 'right', width: 80 },
    { key: 'withPhone', title: 'With a phone', dataIndex: 'withPhone', align: 'right', width: 110 },
    { key: 'queued', title: 'Waiting', dataIndex: 'queued', align: 'right', width: 90 },
    { key: 'filled', title: 'Filled by auto enrichment', dataIndex: 'filledByAuto', align: 'right', width: 130 },
    { key: 'none', title: 'Checked, no phone found', dataIndex: 'checkedNoPhone', align: 'right', width: 130 },
    {
      key: 'action',
      title: '',
      width: 220,
      render: (_: unknown, l: AutoEnrichCompanyLine) => (
        <LineAction
          line={l}
          onRun={() => start.mutate(l.companyId)}
          onStop={() => stop.mutate(l.companyId)}
          running={start.isPending && start.variables === l.companyId}
          stopping={stop.isPending && stop.variables === l.companyId}
        />
      ),
    },
  ];

  return (
    <Card
      size="small"
      title={AUTO_ENRICH_PAST_COPY.title}
      extra={<Button size="small" onClick={() => void refetch()} loading={isFetching && !isLoading}>{AUTO_ENRICH_PAST_COPY.refresh}</Button>}
    >
      <Space direction="vertical" size={space.md} style={{ width: '100%' }}>
        <Alert type="info" showIcon message={AUTO_ENRICH_PAST_COPY.helper} />
        <AsyncBoundary error={error} onRetry={() => void refetch()}>
          <Table<AutoEnrichCompanyLine>
            size="small"
            rowKey="companyId"
            loading={isLoading}
            columns={columns}
            dataSource={data ?? []}
            pagination={false}
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: AUTO_ENRICH_PAST_COPY.noCompanies }}
          />
        </AsyncBoundary>
      </Space>
    </Card>
  );
}
